/**
 * SCRUM-149 — Modelo de capability levels para permisos operativos.
 *
 * Extiende el modelo boolean existente SIN romperlo:
 *   - "none"  = false  → sin acceso
 *   - "read"  = true*  → solo lectura
 *   - "write" = true   → equivale al boolean `true` actual (operar / editar)
 *   - "admin" = true   → control total (incluye acciones destructivas)
 *
 * Backward-compat: cuando no hay nivel almacenado, boolean `true` ↔ "write"
 * (booleanEquivalentLevel del spec). El gate `assertSmStaffPermission` sigue
 * siendo autoritativo para el boolean dimension; `assertSmStaffCapabilityLevel`
 * agrega la verificación de nivel cuando está disponible.
 *
 * Feature-flag: CAPABILITY_LEVELS_FF (env o product flag). Cuando está OFF,
 * toda lógica de nivel es no-op y se cae al modelo boolean.
 *
 * Almacenamiento (pendiente de migración DB):
 *   - Los niveles se persisten en sm_users.permission_capabilities JSONB
 *     (columna nueva — ver migrations/116_permission_capabilities.sql).
 *   - Hasta que la migración se aplique, el nivel se deriva en runtime desde
 *     el boolean via booleanEquivalentLevel (graceful degradation).
 */
import type { OperationalPermission } from "./operational-permissions";

// ─── Tipo central ──────────────────────────────────────────────────────────

export type CapabilityLevel = "none" | "read" | "write" | "admin";

export const CAPABILITY_LEVEL_ORDER: CapabilityLevel[] = ["none", "read", "write", "admin"];

export const CAPABILITY_LEVEL_LABELS: Record<CapabilityLevel, string> = {
  none: "Sin acceso",
  read: "Solo lectura",
  write: "Puede operar",
  admin: "Control total",
};

export const CAPABILITY_LEVEL_SHORT_LABELS: Record<CapabilityLevel, string> = {
  none: "Sin acceso",
  read: "Lectura",
  write: "Edición",
  admin: "Admin",
};

// ─── Spec por permiso ──────────────────────────────────────────────────────

/**
 * Declara qué capacidades soporta cada permiso y qué significa cada nivel.
 *
 * - supportsSubLevels: si `true`, la UI puede ofrecer selector (read/write/admin)
 * - booleanEquivalentLevel: nivel que se asume cuando el override boolean es `true`
 * - readDescription / writeDescription / adminDescription: descripción del nivel
 */
export interface PermissionCapabilitySpec {
  permission: OperationalPermission;
  supportsSubLevels: boolean;
  booleanEquivalentLevel: Exclude<CapabilityLevel, "none">;
  readDescription?: string;
  writeDescription?: string;
  adminDescription?: string;
}

export const PERMISSION_CAPABILITY_SPECS: Record<OperationalPermission, PermissionCapabilitySpec> =
  {
    can_manage_catalog: {
      permission: "can_manage_catalog",
      supportsSubLevels: true,
      booleanEquivalentLevel: "write",
      readDescription: "Ver productos y precios sin modificar",
      writeDescription: "Crear y editar productos y precios",
      adminDescription: "Crear, editar y eliminar productos; cambios masivos",
    },
    can_manage_orders: {
      permission: "can_manage_orders",
      supportsSubLevels: true,
      booleanEquivalentLevel: "write",
      readDescription: "Ver órdenes y su estado",
      writeDescription: "Confirmar y cambiar estado de órdenes",
      adminDescription: "Editar, reasignar y cancelar cualquier orden",
    },
    can_manage_team: {
      permission: "can_manage_team",
      supportsSubLevels: true,
      booleanEquivalentLevel: "admin",
      readDescription: "Ver el equipo y sus roles",
      writeDescription: "Invitar usuarios y asignar roles",
      adminDescription: "Invitar, editar, desactivar y eliminar usuarios; gestionar roles",
    },
    can_manage_branch: {
      permission: "can_manage_branch",
      supportsSubLevels: true,
      booleanEquivalentLevel: "write",
      readDescription: "Ver sucursales y su configuración",
      writeDescription: "Editar sucursales y horarios",
      adminDescription: "Crear, editar y eliminar sucursales",
    },
    can_manage_tables: {
      permission: "can_manage_tables",
      supportsSubLevels: true,
      booleanEquivalentLevel: "write",
      readDescription: "Ver mesas y reservas del salón",
      writeDescription: "Gestionar mesas, reservas y disponibilidad",
      adminDescription: "Administrar layout y eliminar mesas",
    },
    can_manage_schedule: {
      permission: "can_manage_schedule",
      supportsSubLevels: true,
      booleanEquivalentLevel: "write",
      readDescription: "Ver agenda y horarios",
      writeDescription: "Gestionar agenda, horarios y disponibilidad",
      adminDescription: "Administrar agenda, bloqueos y configuración",
    },
    can_manage_branding: {
      permission: "can_manage_branding",
      supportsSubLevels: true,
      booleanEquivalentLevel: "write",
      readDescription: "Ver la configuración de marca y tienda pública",
      writeDescription: "Editar marca, tienda pública y contenidos",
      adminDescription: "Control total de marca, dominio y configuración pública",
    },
    can_view_metrics: {
      permission: "can_view_metrics",
      supportsSubLevels: false,
      booleanEquivalentLevel: "read",
      readDescription: "Ver métricas e informes del negocio",
    },
    can_view_customer_phone: {
      permission: "can_view_customer_phone",
      supportsSubLevels: false,
      booleanEquivalentLevel: "read",
      readDescription: "Ver número de teléfono de los clientes",
    },
    can_refund_order: {
      permission: "can_refund_order",
      supportsSubLevels: false,
      booleanEquivalentLevel: "write",
      writeDescription: "Emitir reembolsos y devoluciones",
    },
    can_override_stock: {
      permission: "can_override_stock",
      supportsSubLevels: false,
      booleanEquivalentLevel: "write",
      writeDescription: "Ajustar manualmente el stock de productos",
    },
    can_print_kitchen: {
      permission: "can_print_kitchen",
      supportsSubLevels: false,
      booleanEquivalentLevel: "write",
      writeDescription: "Imprimir comandas y tickets de cocina",
    },
    can_send_campaigns: {
      permission: "can_send_campaigns",
      supportsSubLevels: false,
      booleanEquivalentLevel: "write",
      writeDescription: "Crear y enviar campañas y mensajes a clientes",
    },
    can_manage_billing: {
      permission: "can_manage_billing",
      supportsSubLevels: false,
      booleanEquivalentLevel: "admin",
      adminDescription: "Gestionar suscripción, facturación y métodos de pago",
    },
    can_manage_integrations: {
      permission: "can_manage_integrations",
      supportsSubLevels: false,
      booleanEquivalentLevel: "admin",
      adminDescription: "Gestionar integraciones de pago y servicios externos",
    },
    can_confirm_booking: {
      permission: "can_confirm_booking",
      supportsSubLevels: false,
      booleanEquivalentLevel: "write",
      writeDescription: "Confirmar, rechazar y gestionar turnos y reservas",
    },
    can_operate_pos: {
      permission: "can_operate_pos",
      supportsSubLevels: false,
      booleanEquivalentLevel: "write",
      writeDescription: "Cobrar y registrar ventas en el punto de venta",
    },
    can_approve_pos_product_edit: {
      permission: "can_approve_pos_product_edit",
      supportsSubLevels: false,
      booleanEquivalentLevel: "admin",
      adminDescription: "Aprobar edición de productos (precio/datos) desde el ticket del POS",
    },
  };

// ─── Helpers ────────────────────────────────────────────────────────────────

/** true si `actual` satisface el nivel `required` (mayor o igual en la escala). */
export function capabilityLevelSatisfies(
  actual: CapabilityLevel,
  required: CapabilityLevel,
): boolean {
  return CAPABILITY_LEVEL_ORDER.indexOf(actual) >= CAPABILITY_LEVEL_ORDER.indexOf(required);
}

/** Convierte un CapabilityLevel al boolean equivalente (backward compat). */
export function capabilityLevelToBoolean(level: CapabilityLevel): boolean {
  return level !== "none";
}

/**
 * Resuelve el CapabilityLevel efectivo de un permiso dado, de más a menos específico:
 * 1. Nivel almacenado explícito por USUARIO (override individual) → lo usa.
 * 2. Boolean override por usuario: true → booleanEquivalentLevel; false → "none".
 * 3. Nivel elegido en el ROL para este permiso (`roleLevel`, SCRUM-477/478/479 —
 *    antes el rol solo guardaba presencia/ausencia, ahora también el nivel
 *    read/write/admin cuando `spec.supportsSubLevels`) → lo usa.
 * 4. Si el rol solo tiene presencia (sin nivel elegido) → booleanEquivalentLevel.
 * 5. Sin nada de lo anterior → "none".
 */
export function resolveCapabilityLevel(
  permission: OperationalPermission,
  opts: {
    storedLevel?: CapabilityLevel | null;
    booleanOverride?: boolean | null;
    roleHasPermission: boolean;
    roleLevel?: CapabilityLevel | null;
  },
): CapabilityLevel {
  const spec = PERMISSION_CAPABILITY_SPECS[permission];
  if (opts.storedLevel != null && opts.storedLevel !== "none") return opts.storedLevel;
  if (opts.booleanOverride === true) return spec.booleanEquivalentLevel;
  if (opts.booleanOverride === false) return "none";
  if (!opts.roleHasPermission) return "none";
  if (opts.roleLevel != null && opts.roleLevel !== "none") return opts.roleLevel;
  return spec.booleanEquivalentLevel;
}

/** Valida que un valor es un CapabilityLevel válido. */
export function isCapabilityLevel(value: unknown): value is CapabilityLevel {
  return (
    value === "none" || value === "read" || value === "write" || value === "admin"
  );
}

/** Devuelve la descripción del nivel para un permiso dado. */
export function capabilityLevelDescription(
  spec: PermissionCapabilitySpec,
  level: CapabilityLevel,
): string | undefined {
  if (level === "read") return spec.readDescription;
  if (level === "write") return spec.writeDescription;
  if (level === "admin") return spec.adminDescription;
  return undefined;
}
